import { formatAED, type AdminBusinessSummary } from "@teamna/shared";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Badge,
  DataTable,
  ErrorState,
  PageHeader,
  Select,
  StatTile,
  Toolbar,
  type ChipOption,
  type DataTableColumn,
} from "@teamna/ui/components/index.ts";
import { useAdminBusinesses, useBillingOverview } from "../lib/cc-api.ts";
import { BILLING_FILTERS, billingCell, matchesBillingFilter, noPlanOf, type BillingFilter } from "./billing-format.ts";
import { countText, lastActivityText, setupText, sportName } from "./format.ts";
import { heardFromText, matchesSourceFilter, SOURCE_FILTERS, SourceCell, type SourceFilter } from "./signup-source.tsx";

/**
 * Every business on Teamna, in one table (web-app-v2.md section 5).
 *
 * The search, the sport filter and the billing filter run in the browser. The list is tens of rows,
 * and a filter that waits for the API is slower than the founder who types in it.
 *
 * Above the table, the Teamna billing numbers (subscription billing spec, section 6.2): paying
 * businesses, monthly revenue after discounts, trials, and the businesses that need a look.
 */

const ALL_SPORTS = "all";

const COLUMNS: DataTableColumn<AdminBusinessSummary>[] = [
  {
    id: "business",
    header: "Business",
    sortValue: (row) => row.name,
    cell: (row) => (
      <div className="min-w-[150px]">
        <p className="font-semibold text-ink">{row.name}</p>
        <p className="text-[12px] text-ink-3 [overflow-wrap:anywhere]">{row.slug}</p>
      </div>
    ),
  },
  {
    id: "sport",
    header: "Sport",
    sortValue: (row) => row.sport,
    cell: (row) => sportName(row.sport),
  },
  {
    id: "owner",
    header: "Owner",
    sortValue: (row) => row.ownerName ?? row.ownerIdentifier,
    cell: (row) =>
      row.ownerName === null && row.ownerIdentifier === null ? (
        <span className="text-ink-3">No owner</span>
      ) : (
        <div className="min-w-0">
          <p className="text-ink">{row.ownerName ?? "No name"}</p>
          {row.ownerIdentifier ? (
            // An email is one long word; let it break so the table fits a 1280px window.
            <p className="text-[12px] text-ink-3 [overflow-wrap:anywhere]">{row.ownerIdentifier}</p>
          ) : null}
        </div>
      ),
  },
  {
    id: "setup",
    header: "Setup",
    // Sorted by the fraction done, so 8 of 8 is above 6 of 8 whatever the totals are.
    sortValue: (row) => (row.setupTotal === 0 ? 0 : row.setupDone / row.setupTotal),
    cell: (row) => setupText(row.setupDone, row.setupTotal),
  },
  {
    id: "members",
    header: "Members active",
    align: "end",
    sortValue: (row) => row.membersActive,
    cell: (row) => <span className="tabular-nums">{row.membersActive}</span>,
  },
  {
    id: "participants",
    header: "Participants",
    align: "end",
    sortValue: (row) => row.participants,
    cell: (row) => <span className="tabular-nums">{row.participants}</span>,
  },
  {
    id: "staff",
    header: "Staff",
    align: "end",
    sortValue: (row) => row.staffCount,
    cell: (row) => <span className="tabular-nums">{row.staffCount}</span>,
  },
  {
    id: "source",
    header: "Source",
    // Warm intros first, then by the answer.
    sortValue: (row) => `${row.signupSource.warmIntro ? 0 : 1} ${heardFromText(row.signupSource.heardFrom)}`,
    cell: (row) => (row.demoExpiresAt ? <span className="text-ink-3">Demo</span> : <SourceCell source={row.signupSource} />),
  },
  {
    id: "activity",
    header: "Last activity",
    // Newest first reads as "ascending" to nobody, so the value is the time itself; nulls go last.
    sortValue: (row) => (row.lastActivityAt === null ? null : Date.parse(row.lastActivityAt)),
    cell: (row) => (
      <span className={row.lastActivityAt === null ? "text-ink-3" : ""}>
        {lastActivityText(row.lastActivityAt)}
      </span>
    ),
  },
  {
    id: "billing",
    header: "Billing",
    // Grouped by state, then the soonest date first inside a state.
    sortValue: (row) =>
      row.demoExpiresAt ? "z demo" : row.pilotSince ? "x pilot" : row.billing ? `${row.billing.state} ${row.billing.trialEndsAt ?? row.billing.graceEndsAt ?? ""}` : "y none",
    cell: (row) => {
      const cell = billingCell(row.billing, noPlanOf(row), new Date());
      return (
        <div className="flex min-w-[130px] flex-col items-start gap-0.5">
          <Badge tone={cell.tone}>{cell.label}</Badge>
          {cell.detail ? <span className="text-[12px] text-ink-2">{cell.detail}</span> : null}
        </div>
      );
    },
  },
];

function matches(row: AdminBusinessSummary, query: string): boolean {
  if (!query) return true;
  const needle = query.toLowerCase();
  // The referrer name too, so a search for an owner also finds the businesses they referred (task 2.2).
  return [row.name, row.ownerName, row.ownerIdentifier, row.signupSource.referrerName].some(
    (value) => value !== null && value.toLowerCase().includes(needle),
  );
}

export function AdminBusinessesScreen() {
  const businesses = useAdminBusinesses();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [sport, setSport] = useState(ALL_SPORTS);
  const [billing, setBilling] = useState<BillingFilter>("all");
  const [source, setSource] = useState<SourceFilter>("all");
  const overview = useBillingOverview();

  const rows = useMemo(() => businesses.data ?? [], [businesses.data]);

  const sportOptions = useMemo<ChipOption[]>(() => {
    const counts = new Map<string, number>();
    for (const row of rows) counts.set(row.sport, (counts.get(row.sport) ?? 0) + 1);
    const sports = [...counts.keys()].sort();
    return [
      { id: ALL_SPORTS, label: "All sports", count: rows.length },
      ...sports.map((id) => ({ id, label: sportName(id), count: counts.get(id) ?? 0 })),
    ];
  }, [rows]);

  const visible = useMemo(
    () =>
      rows.filter(
        (row) =>
          (sport === ALL_SPORTS || row.sport === sport) &&
          matchesBillingFilter(row, billing, new Date()) &&
          matchesSourceFilter(row.signupSource, source) &&
          matches(row, query.trim()),
      ),
    [rows, sport, billing, source, query],
  );

  const description = businesses.data
    ? `${countText(rows.length, "business", "businesses")} on Teamna`
    : undefined;

  return (
    <>
      <PageHeader title="Businesses" description={description} />
      {overview.data?.enabled ? (
        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatTile label="Paying businesses" value={overview.data.paying} />
          <StatTile label="Monthly revenue" value={formatAED(overview.data.monthlyRevenueFils)} delta="After discounts" />
          <StatTile label="Trials" value={overview.data.trialing} delta={`${overview.data.trialEndingSoon} end in 3 days or less`} />
          <StatTile
            label="Past due or read-only"
            value={overview.data.pastDueOrReadOnly}
            delta={`${overview.data.pastDue} past due, ${overview.data.readOnly} read-only`}
            tone={overview.data.pastDueOrReadOnly > 0 ? "bad" : "neutral"}
          />
        </div>
      ) : null}
      {businesses.error && !businesses.data ? (
        <ErrorState error={businesses.error} onRetry={() => void businesses.refetch()} />
      ) : (
        <>
          <Toolbar
            search={{
              value: query,
              onChange: setQuery,
              placeholder: "Search by business, owner or referrer",
            }}
            filters={{
              options: sportOptions,
              selectedId: sport,
              onSelect: setSport,
              label: "Filter by sport",
            }}
          >
            <Select label="Filter by billing" value={billing} onChange={(event) => setBilling(event.target.value as BillingFilter)} className="sm:max-w-[260px]">
              {BILLING_FILTERS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Select label="Filter by source" value={source} onChange={(event) => setSource(event.target.value as SourceFilter)} className="sm:max-w-[260px]">
              {SOURCE_FILTERS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Toolbar>
          <DataTable
            columns={COLUMNS}
            rows={visible}
            rowKey={(row) => row.id}
            rowLabel={(row) => row.name}
            onRowClick={(row) =>
              void navigate({ to: "/businesses/$id", params: { id: row.id } })
            }
            isLoading={businesses.isLoading}
            defaultSort={{ id: "business", direction: "asc" }}
            countLabel={(count) => countText(count, "business", "businesses")}
            caption="Businesses on Teamna"
            density="compact"
            empty={
              rows.length === 0
                ? { title: "No businesses yet", message: "No business has signed up to Teamna." }
                : {
                    title: "No business matches",
                    message: "Change the search, the sport, the billing or the source filter.",
                  }
            }
          />
        </>
      )}
    </>
  );
}
